import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Activity,
  Building2,
  CreditCard,
  Headphones,
  Inbox,
  LayoutDashboard,
  Layers3,
  LogOut,
  Mail,
  Menu,
  ScrollText,
  ShieldCheck,
  Users,
  Volume2,
  VolumeX,
  Wrench,
  X,
} from "lucide-react";
import { CONSOLE_SIDEBAR_GROUPS, getConsolePageTitle } from "./navCatalog.js";
import { useSession } from "./session/SessionContext.js";
import { useRequestsBadge } from "../modules/finanzas/useRequestsBadge.js";
import { useSupportBadge, isAnyMuted, setMuted } from "../modules/soporte/index.js";

const ICONS = {
  LayoutDashboard,
  Activity,
  Building2,
  CreditCard,
  Layers3,
  Mail,
  Headphones,
  Inbox,
  ScrollText,
  Users,
  Wrench,
};

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `flex min-h-11 min-w-0 items-center gap-3 rounded-input px-3 py-2.5 text-sm font-medium transition-colors ${
    isActive
      ? "bg-surface-elevated text-fg shadow-card"
      : "text-fg-muted active:bg-surface-elevated/60 hover:bg-surface-elevated/60 hover:text-fg"
  }`;

function SidebarInsides({ onNavigate, onLogout }: { onNavigate: () => void; onLogout: () => void }) {
  const { user, can } = useSession();
  const { total: supportUnread } = useSupportBadge();
  const requestsPending = useRequestsBadge(can("finanzas"));
  const [audioMuted, setAudioMuted] = useState<boolean>(() => isAnyMuted());
  const toggleAudioMute = () => {
    const next = !audioMuted;
    setMuted("new", next);
    setMuted("add", next);
    setAudioMuted(next);
  };

  const visibleGroups = CONSOLE_SIDEBAR_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => can(item.module)),
  })).filter((group) => group.items.length > 0);

  return (
    <>
      <div className="border-b border-border p-4 pt-[max(1rem,env(safe-area-inset-top,0px))] lg:pt-4">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-input bg-brand-700 text-fg-on-brand">
            <ShieldCheck className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0 leading-tight">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-500">RediPOS</p>
            <p className="text-sm font-semibold text-fg">ERP</p>
          </div>
        </div>
      </div>
      <nav className="flex flex-1 flex-col space-y-4 overflow-y-auto overflow-x-hidden overscroll-y-contain p-2">
        {visibleGroups.map((group) => (
          <div key={group.label}>
            <p className="mb-1.5 px-3 text-[10px] font-bold uppercase tracking-wider text-fg-subtle">
              {group.label}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = (ICONS as Record<string, (typeof ICONS)[keyof typeof ICONS]>)[item.icon];
                const isSupport = item.to === "/soporte";
                const isRequests = item.to === "/solicitudes";
                return (
                  <NavLink
                    key={item.to + (item.end ? "-root" : "")}
                    to={item.to}
                    end={item.end}
                    className={linkClass}
                    onClick={onNavigate}
                  >
                    {Icon ? <Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden /> : null}
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {isSupport && supportUnread > 0 ? (
                      <span
                        aria-label={`${supportUnread} mensajes de soporte sin leer`}
                        className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-danger px-1.5 py-0.5 text-[10px] font-bold text-white"
                      >
                        {supportUnread > 99 ? "99+" : supportUnread}
                      </span>
                    ) : null}
                    {isRequests && requestsPending > 0 ? (
                      <span
                        aria-label={`${requestsPending} solicitudes de clientes pendientes`}
                        className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-danger px-1.5 py-0.5 text-[10px] font-bold text-white"
                      >
                        {requestsPending > 99 ? "99+" : requestsPending}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="space-y-1 border-t border-border p-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-fg-subtle">Sesión</p>
        <p className="truncate text-xs text-fg-muted" title={user?.email}>
          {user?.email}
        </p>
      </div>
      <button
        type="button"
        onClick={toggleAudioMute}
        title={audioMuted ? "Activar sonidos de soporte" : "Silenciar sonidos de soporte"}
        aria-label={audioMuted ? "Activar sonidos de soporte" : "Silenciar sonidos de soporte"}
        aria-pressed={audioMuted}
        className={`mx-2 mb-1 flex min-h-11 min-w-0 items-center justify-center gap-2 rounded-input py-2.5 text-sm transition-colors active:bg-surface-elevated hover:bg-surface-elevated ${
          audioMuted ? "text-fg-muted" : "text-brand-500"
        }`}
      >
        {audioMuted ? <VolumeX className="h-4 w-4" aria-hidden /> : <Volume2 className="h-4 w-4" aria-hidden />}
        {audioMuted ? "Sonidos silenciados" : "Silenciar sonidos"}
      </button>
      <button
        type="button"
        onClick={onLogout}
        className="m-2 mt-0 flex min-h-11 min-w-0 items-center justify-center gap-2 rounded-input py-2.5 text-sm text-fg-muted transition-colors active:bg-surface-elevated hover:bg-surface-elevated hover:text-fg"
      >
        <LogOut className="h-4 w-4" aria-hidden />
        Salir
      </button>
    </>
  );
}

export default function Shell() {
  const { logout } = useSession();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const title = getConsolePageTitle(pathname);

  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  return (
    <div className="relative min-h-dvh w-full min-w-0 bg-zinc-950">
      {drawerOpen && (
        <button
          type="button"
          className="fixed inset-0 z-40 cursor-default bg-zinc-950/70 backdrop-blur-[2px] lg:hidden"
          aria-label="Cerrar menú"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <div className="flex min-h-dvh w-full min-w-0 flex-col lg:flex-row">
        <aside
          className={[
            "z-50 flex w-[min(20rem,88vw)] max-w-full shrink-0 flex-col border-r border-border bg-surface",
            "transform transition-transform duration-200 ease-out will-change-transform",
            "fixed inset-y-0 left-0 lg:static lg:z-auto lg:max-w-none lg:w-60 lg:translate-x-0",
            drawerOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          ].join(" ")}
          id="erp-nav"
          aria-label="Navegación"
        >
          <SidebarInsides onNavigate={() => setDrawerOpen(false)} onLogout={logout} />
          <div className="border-t border-border p-2 pb-[max(0.5rem,env(safe-area-inset-bottom,0px))] lg:hidden">
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-input text-sm text-fg-muted hover:bg-surface-elevated"
            >
              <X className="h-4 w-4" />
              Cerrar
            </button>
          </div>
        </aside>

        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-30 border-b border-border bg-surface/90 backdrop-blur-sm supports-backdrop-filter:bg-surface/75 lg:hidden">
            <div className="flex h-12 min-h-12 max-w-full items-center gap-2 pl-[max(0.75rem,env(safe-area-inset-left,0px))] pr-3 pt-[max(0px,env(safe-area-inset-top,0px))]">
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-input text-fg hover:bg-surface-elevated"
                aria-controls="erp-nav"
                aria-expanded={drawerOpen}
              >
                <Menu className="h-5 w-5" aria-hidden />
                <span className="sr-only">Abrir menú</span>
              </button>
              <h1 className="min-w-0 flex-1 truncate text-base font-semibold text-fg">{title}</h1>
            </div>
          </header>

          <main className="min-h-0 min-w-0 flex-1 overflow-auto">
            <div
              className="mx-auto w-full max-w-6xl px-4 py-4 sm:px-5 sm:py-6 lg:px-8 lg:py-8"
              style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom, 0px))" }}
            >
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
